import { ImagePaths } from '../../lib/imageUrls.js';

export const purviewDataProtectionPractitioners = {

    id: 40,
    slug: 'purview-data-protection-practitioners',
    ogImage: '/og/courses/purview-data-protection-practitioners.jpg',
    courseCode: 'PURVIEW-DPP-001',
    featured: true,
    title: 'Microsoft Purview for Data Protection Practitioners',
    subtitle: 'Deploy custom SITs, a four-tier label taxonomy, and DLP across Microsoft 365',
    organization: 'EDUC4TE',
    valueProposition: 'Build a defensible foundation in Microsoft Purview data protection — deploy custom Sensitive Information Types, a four-tier sensitivity label taxonomy with encryption and visual markings, and Data Loss Prevention across email, SharePoint, OneDrive, and Teams. Aligned to the Privacy Act 1988, OAIC APP 11, and the ACSC Information Security Manual, this one-day foundation contributes 25% towards SC-401.',
    duration: '8 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_2,
    shortDescription: 'PURVIEW-DPP-001 — Purview data protection covering custom SITs, four-tier label taxonomy, and DLP for M365 admins under Privacy Act 1988 and OAIC APP 11.',
    price: {
      aud: 1200,
      currency: 'AUD'
    },

    stripeConfig: {
    priceId: 'price_1Te5C3Ku1nyc322UK8UqYvMr',
    productId: 'prod_UapkvIbMmQImBn'
  },

    snapshot: {
      whoItsFor: [
        'IT administrators deploying basic Microsoft Purview data protection',
        'Microsoft 365 administrators with a compliance and information protection remit',
        'Information security analysts responsible for classification, labelling, and DLP',
        'SC-401 candidates building foundation skills for Administering Information Security in Microsoft 365',
        'Privacy and risk officers translating Privacy Act 1988 obligations into Purview controls'
      ],
      accreditation: 'Aligned to Microsoft Purview Information Protection learning paths; contributes 25% towards Microsoft Certified: Information Security Administrator Associate (SC-401)'
    },

    logistics: {
      classSize: 'One-day intensive — virtual cohort with hands-on labs and live Q&A',
      requirements: [
        'Working knowledge of Microsoft 365 (Exchange, SharePoint, OneDrive, Teams)',
        'Familiarity with the Microsoft Purview portal and basic compliance concepts',
        'Access to a Microsoft 365 E5 (or Compliance add-on) tenant for labs',
        'Completion of Module 1 — Microsoft Purview in a Day for Executives, or 6+ months of M365 admin experience',
        'Basic understanding of Australian privacy obligations under the Privacy Act 1988'
      ]
    },

    overview: 'Microsoft Purview for Data Protection Practitioners is Module 2 of the six-part EDUC4TE Purview Training Track. This one-day, 8-hour foundation enables IT administrators to deploy and operate basic Purview data protection across Microsoft 365. You will deploy custom Sensitive Information Types tailored to Australian data requirements (TFN, Medicare, ABN, driver licence) alongside built-in Australian SITs, design and implement a four-tier sensitivity label taxonomy with encryption and visual markings, publish labels and configure auto-labelling policies, and configure Data Loss Prevention across email (Exchange), SharePoint, OneDrive, and Teams. Throughout, you will monitor label coverage and DLP events using Content Explorer and Activity Explorer, and integrate Purview with Entra ID and SharePoint governance workflows. The programme maps controls to the Privacy Act 1988, OAIC APP 11, and the ACSC Information Security Manual, and contributes 25% towards the SC-401 Administering Information Security in Microsoft 365 exam. Endpoint and cross-cloud DLP and Adaptive Protection are covered in later modules of the track.',

    outcomes: [
      'Deploy custom Sensitive Information Types tailored to Australian data requirements (TFN, Medicare, ABN, driver licence)',
      'Design and implement a four-tier sensitivity label taxonomy with encryption and visual markings',
      'Configure DLP policies across email, SharePoint, OneDrive, and Teams',
      'Publish sensitivity labels and configure auto-labelling policies',
      'Monitor label coverage and DLP events using Activity Explorer and Content Explorer',
      'Integrate Microsoft Purview with Entra ID and SharePoint governance workflows'
    ],

    audience: [
      'IT administrators',
      'Microsoft 365 administrators with a compliance remit',
      'Information security analysts',
      'SC-401 candidates',
      'Privacy, risk, and compliance officers operating in Australian regulated industries'
    ],

    prerequisites: [
      'Practical Microsoft 365 administration experience (6+ months) across Exchange, SharePoint, and Teams',
      'Exposure to the Microsoft Purview portal and information protection concepts',
      'Awareness of the Privacy Act 1988 and OAIC Australian Privacy Principles',
      'Recommended: completion of Module 1 — Microsoft Purview in a Day for Executives',
      'Access to a Microsoft 365 E5 or Compliance add-on tenant for hands-on labs'
    ],

    modules: [
      {
        number: 1,
        title: 'Classification foundations',
        duration: '120 min',
        topics: [
          'Deploy custom Sensitive Information Types (SITs) tailored to Australian data requirements — TFN, Medicare, ABN, and driver licence',
          'Use built-in Australian sensitive information types and understand when to extend them with custom SITs',
          'Monitor classification coverage with Content Explorer and Activity Explorer',
          'Map classification to OAIC APP 11 (security of personal information) and Privacy Act 1988 obligations',
          'Reference: Microsoft Learn — Sensitive information types and Microsoft Purview Information Protection (updated 2025/2026)'
        ]
      },
      {
        number: 2,
        title: 'Sensitivity labels',
        duration: '180 min',
        topics: [
          'Design and implement a four-tier sensitivity label taxonomy for an Australian organisation',
          'Configure encryption and visual markings (headers, footers, watermarks) on each label',
          'Publish labels to users via label policies and configure default labelling',
          'Configure auto-labelling policies to apply labels at scale based on content',
          'Map labels to OAIC APP 11 and Privacy Act 1988 obligations (Reference: Microsoft Learn — Learn about sensitivity labels, updated 2025/2026)'
        ]
      },
      {
        number: 3,
        title: 'Basic DLP',
        duration: '180 min',
        topics: [
          'Configure DLP policies across email (Exchange), SharePoint, OneDrive, and Teams',
          'Author DLP rules: conditions, exceptions, policy tips, and user education prompts',
          'Integrate Microsoft Purview with Entra ID and SharePoint governance workflows',
          'Monitor DLP events using Activity Explorer and DLP alerts',
          'How this foundation contributes 25% towards the SC-401 Administering Information Security in Microsoft 365 exam (Reference: Microsoft Learn — Learn about Data Loss Prevention, updated 2025/2026)'
        ]
      }
    ],

    format: {
      type: 'One-day intensive (virtual), or lunch-and-learn (8 x 1-hour sessions over 8 days)',
      includes: [
        '8 hours of instructor-led training in a single intensive day',
        'Hands-on labs in a Microsoft 365 E5 compliance tenant',
        'Custom and built-in Australian SIT pattern library (TFN, Medicare, ABN, driver licence)',
        'Four-tier sensitivity label taxonomy worksheet aligned to Australian record and data classes',
        'Basic DLP rule pack for email, SharePoint, OneDrive, and Teams with Australian data examples',
        'Entra ID and SharePoint governance integration checklist',
        'SC-401 study planner showing how this module contributes 25% coverage',
        'Certificate of completion suitable for CPD claims'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Register interest for the next one-day intensive — small cohort to support hands-on Purview labs'
    },

    certification: 'Educ4te Certificate of Completion — Microsoft Purview for Data Protection Practitioners. This one-day foundation contributes 25% towards preparation for Microsoft Certified: Information Security Administrator Associate (SC-401 Administering Information Security in Microsoft 365), which replaced the retired SC-400 on 31 May 2025.',

    faqs: [
      {
        question: 'Do I need a Microsoft 365 E5 tenant to follow the labs?',
        answer: 'Yes — auto-labelling, label encryption, and DLP across the Microsoft 365 workloads require Microsoft 365 E5 or the Microsoft 365 E5 Compliance add-on. A developer tenant with the Compliance add-on is sufficient for the labs.'
      },
      {
        question: 'How does this course align to SC-401?',
        answer: 'Microsoft retired SC-400 Information Protection and Compliance Administrator on 31 May 2025 and replaced it with SC-401 Administering Information Security in Microsoft 365. This one-day foundation contributes roughly 25% towards the SC-401 skills outline, covering classification, sensitivity labels, and basic DLP. The remaining coverage is delivered across the other modules of the Purview Training Track.'
      },
      {
        question: 'Does this course cover endpoint and cross-cloud DLP?',
        answer: 'No. This module focuses on basic DLP across email, SharePoint, OneDrive, and Teams. Endpoint DLP and cross-cloud (SaaS) DLP are covered in Module 3 — Endpoint Security, which builds on the classification and DLP foundations established here.'
      },
      {
        question: 'Does this course cover Adaptive Protection?',
        answer: 'No. Adaptive Protection is covered in Module 4 — Insider Risk Management (IRM), where Insider Risk signals drive dynamic, risk-based enforcement. This module establishes the underlying sensitivity labels and DLP policies that Adaptive Protection later builds upon.'
      },
      {
        question: 'How is this different from Module 3 — Endpoint Security?',
        answer: 'This module — Module 2 — establishes classification, a four-tier label taxonomy, and basic DLP across the core Microsoft 365 services. Module 3 — Endpoint Security extends data protection to devices with endpoint DLP and cross-cloud SaaS DLP. Most organisations complete this foundation first.'
      },
      {
        question: 'Where does retention fit in?',
        answer: 'Retention is a separate elective in the Purview Training Track focused on keeping the right data for the right length of time and disposing of it defensibly. This module is about protecting that data while you hold it, and the two are designed to complement each other.'
      },
      {
        question: 'How does the Privacy Act 1988 and APP 11 apply to this course?',
        answer: 'The labs map your sensitivity labels and DLP policies directly to OAIC APP 11 (security of personal information) and Privacy Act 1988 obligations, and to the ACSC Information Security Manual, so you can demonstrate evidence-backed controls over Australian personal information.'
      },
      {
        question: 'Do you offer group or cohort options for our organisation?',
        answer: 'Yes. We run private cohorts for organisations that want to bring a team through together, with optional tailoring to your tenant configuration, label taxonomy, and Australian regulatory context. Contact us to discuss a private quote and scheduling.'
      },
      {
        question: 'Is this course eligible for Continuing Professional Development (CPD) claims?',
        answer: 'Yes. Participants receive an Educ4te Certificate of Completion that records the 8 contact hours, learning outcomes, and topics covered. The certificate is suitable for CPD claims under most Australian professional schemes, including IAPP, ISACA, (ISC)2, and the OAIC-aligned privacy professional bodies.'
      },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ],

    australianContext: true,
    complianceFrameworks: ['Privacy Act 1988', 'OAIC APP 11', 'Notifiable Data Breaches scheme', 'ACSC ISM']
};
